// app/features/auth/msalConfig.ts
//
// Single source of truth for MSAL.js configuration. Multi-tenant; PKCE auth-
// code flow; sessionStorage cache (per-tab, cleared on close).

import {
  PublicClientApplication,
  type Configuration,
  type RedirectRequest,
} from "@azure/msal-browser";

// Public URL of the deployed app. In production this is the custom domain
// (e.g. https://infocenter.csnx-dci.app); pinning it here means Entra always
// sees the same redirect URI even if a user visits via the Cloud Run host.
// In dev (where the env var isn't set) we fall back to window.location.origin.
const appBaseUrl =
  import.meta.env.VITE_APP_BASE_URL ||
  (typeof window !== "undefined" ? window.location.origin : "http://localhost");

export const msalConfig: Configuration = {
  auth: {
    clientId: import.meta.env.VITE_ENTRA_CLIENT_ID ?? "",
    authority: "https://login.microsoftonline.com/common",
    redirectUri: `${appBaseUrl}/auth/callback`,
    postLogoutRedirectUri: `${appBaseUrl}/login`,
  },
  cache: {
    cacheLocation: "sessionStorage",
    // Mirror the redirect request state into a short-lived cookie so it
    // survives the Entra round-trip even when sessionStorage isn't usable
    // on return (browser tracking protection / storage partitioning).
    // Without this, an already-signed-in user can hang on /auth/callback
    // because MSAL can't find the state to consume the response.
    storeAuthStateInCookie: true,
  },
};

export const loginRequest: RedirectRequest = {
  scopes: ["openid", "profile", "email"],
};

export const msalInstance = new PublicClientApplication(msalConfig);
